import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import TopHeader from '../components/TopHeader';
import { accountingAPI, contractorPaymentAPI } from '../utils/api';
import {
  DollarSign, TrendingUp, TrendingDown, CreditCard,
  PieChart, BarChart3, AlertCircle, Clock, Calendar,
  ArrowUpRight, ArrowDownRight, Receipt, Wallet, Target,
  ChevronRight, Filter, Download, UserCheck, CheckCircle,
  X, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// Date range presets
const DATE_PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'This Quarter', value: 'quarter' },
  { label: 'This Year', value: 'year' },
  { label: 'All Time', value: 'all' },
];

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

// Format percentage
const formatPercent = (value) => {
  return `${(value || 0).toFixed(1)}%`;
};

// Premium Metric Card Component with minimal, elegant design
const MetricCard = ({ title, value, icon: Icon, trend, trendValue, subtitle, variant = 'default', onClick }) => {
  // Premium muted color scheme aligned with portal theme
  const variantStyles = {
    default: {
      bg: 'bg-white',
      border: 'border border-slate-200',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600',
      valueColor: 'text-slate-900',
    },
    revenue: {
      bg: 'bg-white',
      border: 'border-l-4 border-l-teal-500 border border-slate-200',
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-600',
      valueColor: 'text-teal-700',
    },
    collected: {
      bg: 'bg-white',
      border: 'border-l-4 border-l-emerald-500 border border-slate-200',
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      valueColor: 'text-emerald-700',
    },
    pending: {
      bg: 'bg-white',
      border: 'border-l-4 border-l-amber-500 border border-slate-200',
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      valueColor: 'text-amber-700',
    },
    overdue: {
      bg: 'bg-white',
      border: 'border-l-4 border-l-rose-500 border border-slate-200',
      iconBg: 'bg-rose-50',
      iconColor: 'text-rose-600',
      valueColor: 'text-rose-700',
    },
    expense: {
      bg: 'bg-white',
      border: 'border-l-4 border-l-slate-500 border border-slate-200',
      iconBg: 'bg-slate-50',
      iconColor: 'text-slate-600',
      valueColor: 'text-slate-700',
    },
    fixed: {
      bg: 'bg-white',
      border: 'border-l-4 border-l-blue-500 border border-slate-200',
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      valueColor: 'text-blue-700',
    },
    variable: {
      bg: 'bg-white',
      border: 'border-l-4 border-l-purple-500 border border-slate-200',
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      valueColor: 'text-purple-700',
    },
    profit: {
      bg: 'bg-white',
      border: 'border-l-4 border-l-teal-600 border border-slate-200',
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-600',
      valueColor: 'text-teal-700',
    },
    loss: {
      bg: 'bg-white',
      border: 'border-l-4 border-l-rose-600 border border-slate-200',
      iconBg: 'bg-rose-50',
      iconColor: 'text-rose-600',
      valueColor: 'text-rose-700',
    },
  };

  const styles = variantStyles[variant] || variantStyles.default;

  return (
    <Card 
      className={`${styles.bg} ${styles.border} shadow-sm cursor-pointer hover:shadow-md transition-all duration-200`}
      onClick={onClick}
      data-testid={`metric-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</p>
            <p className={`text-2xl font-bold mt-1.5 ${styles.valueColor}`}>{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-2.5 ${styles.iconBg} rounded-xl`}>
            <Icon size={20} className={styles.iconColor} />
          </div>
        </div>
        {trend !== undefined && (
          <div className="flex items-center gap-1.5 mt-3 text-xs">
            {trend === 'up' ? (
              <ArrowUpRight size={14} className="text-emerald-500" />
            ) : (
              <ArrowDownRight size={14} className="text-rose-500" />
            )}
            <span className={trend === 'up' ? 'text-emerald-600' : 'text-rose-600'}>
              {trendValue}
            </span>
            <span className="text-slate-400">vs last period</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Mini Chart Component (simplified bar chart)
const MiniBarChart = ({ data, height = 120 }) => {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="flex items-end gap-1.5 h-full" style={{ height }}>
      {data.map((item, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className="w-full bg-teal-500 rounded-t-sm transition-all duration-300 hover:bg-teal-400"
            style={{ height: `${(item.value / maxValue) * 100}%`, minHeight: item.value > 0 ? 4 : 0 }}
            title={`${item.label}: ${formatCurrency(item.value)}`}
          />
          <span className="text-[10px] text-slate-500 truncate max-w-full">{item.label}</span>
        </div>
      ))}
    </div>
  );
};

// Data Table Component
const DataTable = ({ columns, data, onRowClick, emptyMessage = 'No data available' }) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200">
            {columns.map((col, idx) => (
              <th key={idx} className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="py-8 text-center text-slate-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, idx) => (
              <tr 
                key={idx}
                className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((col, colIdx) => (
                  <td key={colIdx} className="py-3 px-4 text-sm text-slate-700">
                    {col.render ? col.render(row) : row[col.accessor]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default function AccountingDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('year');
  const [dashboardData, setDashboardData] = useState(null);
  const [salesAnalytics, setSalesAnalytics] = useState(null);
  const [expenseAnalytics, setExpenseAnalytics] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [salesDistribution, setSalesDistribution] = useState(null);
  // Contractor payments
  const [contractorPayments, setContractorPayments] = useState([]);
  const [contractorSummary, setContractorSummary] = useState(null);
  const [contractorFilter, setContractorFilter] = useState('pending');
  const [payingId, setPayingId] = useState(null);
  const [payModal, setPayModal] = useState(null); // { payment }
  const [payForm, setPayForm] = useState({ method: 'Cash', reference: '' });

  // Calculate date range based on preset
  const getDateRange = (preset) => {
    const today = new Date();
    let startDate, endDate = today.toISOString().split('T')[0];
    
    switch (preset) {
      case 'today':
        startDate = endDate;
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        startDate = weekStart.toISOString().split('T')[0];
        break;
      case 'month':
        startDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
        break;
      case 'quarter':
        const qMonth = Math.floor(today.getMonth() / 3) * 3;
        startDate = `${today.getFullYear()}-${String(qMonth + 1).padStart(2, '0')}-01`;
        break;
      case 'year':
        startDate = `${today.getFullYear()}-01-01`;
        break;
      case 'all':
        startDate = '2020-01-01';
        break;
      default:
        startDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
    }
    
    return { startDate, endDate };
  };

  // Fetch dashboard data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const safe = (p) => p.catch((e) => { console.error(e); return null; });
      try {
        const { startDate, endDate } = getDateRange(dateRange);

        const [dashboard, sales, expenses, forecastData, outstandingData, distribution] = await Promise.all([
          safe(accountingAPI.getDashboard(startDate, endDate)),
          safe(accountingAPI.getSalesAnalytics({ start_date: startDate, end_date: endDate, period: 'month' })),
          safe(accountingAPI.getExpenseAnalytics({ start_date: startDate, end_date: endDate })),
          safe(accountingAPI.getForecast(3)),
          safe(accountingAPI.getOutstandingReport()),
          safe(accountingAPI.getSalesDistribution({ start_date: startDate, end_date: endDate })),
        ]);

        if (dashboard) setDashboardData(dashboard.data);
        if (sales) setSalesAnalytics(sales.data);
        if (expenses) setExpenseAnalytics(expenses.data);
        if (forecastData) setForecast(forecastData.data);
        if (outstandingData) setOutstanding(outstandingData.data);
        if (distribution) setSalesDistribution(distribution.data);

        // Contractor payments (independent of date range)
        const [cpAll, cpSummary] = await Promise.all([
          safe(contractorPaymentAPI.getAll()),
          safe(contractorPaymentAPI.getSummary()),
        ]);
        if (cpAll) setContractorPayments(cpAll.data || []);
        if (cpSummary) setContractorSummary(cpSummary.data);
      } catch (error) {
        console.error('Error fetching accounting data:', error);
        toast.error('Failed to load accounting data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRange]);

  const fetchContractorPayments = async () => {
    const [cpAll, cpSummary] = await Promise.all([
      contractorPaymentAPI.getAll().catch(() => null),
      contractorPaymentAPI.getSummary().catch(() => null),
    ]);
    if (cpAll) setContractorPayments(cpAll.data || []);
    if (cpSummary) setContractorSummary(cpSummary.data);
  };

  const handleMarkPaid = async () => {
    if (!payModal) return;
    setPayingId(payModal.payment.id);
    try {
      await contractorPaymentAPI.markPaid(payModal.payment.id, { payment_method: payForm.method, payment_reference: payForm.reference || null });
      toast.success('Payment marked as paid and expense updated');
      setPayModal(null);
      fetchContractorPayments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark paid');
    } finally {
      setPayingId(null);
    }
  };

  const handleMarkUnpaid = async (id) => {
    setPayingId(id);
    try {
      await contractorPaymentAPI.markUnpaid(id);
      toast.success('Reverted to pending');
      fetchContractorPayments();
    } catch { toast.error('Failed to revert'); }
    finally { setPayingId(null); }
  };

  // Prepare chart data
  const revenueChartData = useMemo(() => {
    if (!salesAnalytics?.by_period) return [];
    return Object.entries(salesAnalytics.by_period)
      .slice(-6)
      .map(([key, data]) => ({
        label: key.slice(-5),
        value: data.collected,
      }));
  }, [salesAnalytics]);

  const expenseChartData = useMemo(() => {
    if (!expenseAnalytics?.by_month) return [];
    return Object.entries(expenseAnalytics.by_month)
      .slice(-6)
      .map(([key, data]) => ({
        label: key.slice(-5),
        value: data.total,
      }));
  }, [expenseAnalytics]);

  // Columns for tables
  const recentSalesColumns = [
    { header: 'Client', accessor: 'client_name' },
    { header: 'Product', accessor: 'visa_type', render: (row) => row.visa_type || row.product || '-' },
    { header: 'Amount', accessor: 'total_amount', render: (row) => formatCurrency(row.total_amount) },
    { 
      header: 'Status', 
      accessor: 'payment_status',
      render: (row) => {
        const statusColors = {
          fully_paid: 'bg-green-100 text-green-700',
          partial_paid: 'bg-yellow-100 text-yellow-700',
          pending: 'bg-slate-100 text-slate-700',
          overdue: 'bg-red-100 text-red-700',
        };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[row.payment_status] || statusColors.pending}`}>
            {row.payment_status?.replace('_', ' ').toUpperCase()}
          </span>
        );
      }
    },
  ];

  const outstandingColumns = [
    { header: 'Client', accessor: 'client_name' },
    { header: 'Due Date', accessor: 'due_date', render: (row) => row.due_date || '-' },
    { header: 'Balance', accessor: 'balance_due', render: (row) => formatCurrency(row.balance_due) },
    { 
      header: 'Status', 
      accessor: 'payment_status',
      render: (row) => {
        const isOverdue = row.payment_status === 'overdue';
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${isOverdue ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
            {isOverdue ? 'OVERDUE' : 'PENDING'}
          </span>
        );
      }
    },
  ];

  const recentExpensesColumns = [
    { header: 'Title', accessor: 'title' },
    { header: 'Category', accessor: 'category', render: (row) => row.category?.replace('_', ' ').toUpperCase() },
    { header: 'Amount', accessor: 'amount', render: (row) => formatCurrency(row.amount) },
    { header: 'Date', accessor: 'date' },
  ];

  const forecastColumns = [
    { header: 'Month', accessor: 'month' },
    { header: 'Projected Expenses', accessor: 'projected_expenses', render: (row) => formatCurrency(row.projected_expenses) },
    { header: 'Note', accessor: 'note' },
  ];

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex flex-col ml-[260px]">
          <TopHeader />
          <main className="flex-1 p-6">
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const revenue = dashboardData?.revenue || {};
  const expenses = dashboardData?.expenses || {};
  const profitability = dashboardData?.profitability || {};
  const recentSales = dashboardData?.recent?.sales || [];
  const recentExpenses = dashboardData?.recent?.expenses || [];

  // Derived profit metrics — resolve new fields with fallback to legacy field names
  const profitValue  = profitability.operating_profit ?? profitability.net_profit ?? 0;
  const profitMarginValue = profitability.operating_margin ?? profitability.profit_margin ?? 0;
  const isProfit = profitability.is_profitable !== undefined ? profitability.is_profitable : profitValue >= 0;

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="accounting-dashboard">
      <Sidebar />
      <div className="flex-1 flex flex-col ml-[260px]">
        <TopHeader />
        <main className="flex-1 p-6 overflow-auto">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Accounting Dashboard</h1>
              <p className="text-sm text-slate-500 mt-1">Financial overview and insights</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-40 bg-white" data-testid="date-range-filter">
                  <Calendar size={16} className="mr-2 text-slate-500" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_PRESETS.map((preset) => (
                    <SelectItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate('/admin/accounting/sales')}>
                <Receipt size={16} />
                Sales
              </Button>
              <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate('/admin/accounting/expenses')}>
                <Wallet size={16} />
                Expenses
              </Button>
              <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate('/admin/accounting/reports')}>
                <BarChart3 size={16} />
                Reports
              </Button>
            </div>
          </div>

          {/* Revenue Metrics Row */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <MetricCard
              title="Total Sales"
              value={formatCurrency(revenue.total_sales)}
              icon={DollarSign}
              variant="revenue"
              subtitle={`MTD: ${formatCurrency(revenue.mtd_sales)}`}
              onClick={() => navigate('/admin/accounting/sales')}
            />
            <MetricCard
              title="Revenue Collected"
              value={formatCurrency(revenue.total_collected)}
              icon={CreditCard}
              variant="collected"
              subtitle={`MTD: ${formatCurrency(revenue.mtd_collected)}`}
              onClick={() => navigate('/admin/accounting/sales?status=fully_paid')}
            />
            <MetricCard
              title="Pending Revenue"
              value={formatCurrency(revenue.pending_revenue)}
              icon={Clock}
              variant="pending"
              subtitle={`${outstanding?.summary?.pending_count || 0} invoices`}
              onClick={() => navigate('/admin/accounting/sales?status=pending')}
            />
            <MetricCard
              title="Overdue Revenue"
              value={formatCurrency(revenue.overdue_revenue)}
              icon={AlertCircle}
              variant="overdue"
              subtitle={`${outstanding?.summary?.overdue_count || 0} overdue`}
              onClick={() => navigate('/admin/accounting/sales?status=overdue')}
            />
          </div>

          {/* Expense Metrics Row */}
          <div className="grid grid-cols-5 gap-4 mb-6">
            <MetricCard
              title="Total Expenses"
              value={formatCurrency(expenses.total_expenses)}
              icon={Receipt}
              variant="expense"
              subtitle={`MTD: ${formatCurrency(expenses.mtd_expenses)}`}
              onClick={() => navigate('/admin/accounting/expenses')}
            />
            <MetricCard
              title="Fixed Expenses"
              value={formatCurrency(expenses.fixed_expenses)}
              icon={Target}
              variant="fixed"
              subtitle="Recurring costs"
              onClick={() => navigate('/admin/accounting/expenses?type=fixed')}
            />
            <MetricCard
              title="Variable Expenses"
              value={formatCurrency(expenses.variable_expenses)}
              icon={TrendingDown}
              variant="variable"
              subtitle="One-time costs"
              onClick={() => navigate('/admin/accounting/expenses?type=variable')}
            />
            <MetricCard
              title="Overdue Expenses"
              value={formatCurrency(expenses.overdue_expenses || 0)}
              icon={AlertCircle}
              variant="overdue"
              subtitle={`${expenses.overdue_count || 0} overdue`}
              onClick={() => navigate('/admin/accounting/expenses?status=overdue')}
            />
            <MetricCard
              title={isProfit ? 'Net Profit' : 'Net Loss'}
              value={formatCurrency(profitValue)}
              icon={isProfit ? TrendingUp : TrendingDown}
              variant={isProfit ? 'profit' : 'loss'}
              subtitle={`Margin: ${formatPercent(profitMarginValue)}`}
              onClick={() => navigate('/admin/accounting/reports')}
            />
            <MetricCard
              title="Due to Contractors"
              value={formatCurrency(contractorSummary?.pending_amount || 0)}
              icon={UserCheck}
              variant={contractorSummary?.pending_count > 0 ? 'overdue' : 'default'}
              subtitle={`${contractorSummary?.pending_count || 0} unpaid fees · ₹${((contractorSummary?.paid_this_month || 0) / 1000).toFixed(1)}K paid this month`}
              onClick={() => {
                const tabEl = document.querySelector('[value="contractor"]');
                if (tabEl) tabEl.click();
              }}
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <BarChart3 size={16} className="text-teal-600" />
                  Revenue Trend
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {revenueChartData.length > 0 ? (
                  <MiniBarChart data={revenueChartData} height={150} />
                ) : (
                  <div className="h-[150px] flex items-center justify-center text-slate-400 text-sm">
                    No revenue data for this period
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <BarChart3 size={16} className="text-slate-600" />
                  Expense Trend
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {expenseChartData.length > 0 ? (
                  <MiniBarChart data={expenseChartData} height={150} />
                ) : (
                  <div className="h-[150px] flex items-center justify-center text-slate-400 text-sm">
                    No expense data for this period
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <PieChart size={16} className="text-emerald-600" />
                  Sales by Visa Type
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {salesDistribution?.distribution?.length > 0 ? (
                  <div className="h-[150px] flex flex-col justify-center gap-2">
                    {salesDistribution.distribution.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="flex-1">
                          <div className="flex items-center justify-between text-xs mb-0.5">
                            <span className="text-slate-600 truncate max-w-[120px]" title={item.name}>{item.name}</span>
                            <span className="text-slate-800 font-medium">{item.percentage}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-teal-500 rounded-full transition-all" 
                              style={{ width: `${item.percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    {salesDistribution.distribution.length > 5 && (
                      <div className="text-[10px] text-slate-400 text-center">
                        +{salesDistribution.distribution.length - 5} more
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-[150px] flex items-center justify-center text-slate-400 text-sm">
                    No sales data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Tables Row */}
          <Tabs defaultValue="sales" className="space-y-4">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="sales" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
                Recent Sales
              </TabsTrigger>
              <TabsTrigger value="outstanding" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
                Outstanding Payments
              </TabsTrigger>
              <TabsTrigger value="expenses" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
                Recent Expenses
              </TabsTrigger>
              <TabsTrigger value="forecast" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
                Expense Forecast
              </TabsTrigger>
              <TabsTrigger value="contractor" className="data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700 relative">
                Contractor Payables
                {(contractorSummary?.pending_count || 0) > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-amber-500 text-white text-xs rounded-full font-bold">
                    {contractorSummary.pending_count}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sales">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base font-semibold text-slate-700">Recent Sales</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/admin/accounting/sales')}>
                    View All <ChevronRight size={16} />
                  </Button>
                </CardHeader>
                <CardContent>
                  <DataTable 
                    columns={recentSalesColumns} 
                    data={recentSales.slice(0, 5)}
                    emptyMessage="No sales recorded yet"
                    onRowClick={(row) => row.case_id && navigate(`/admin/cases/${row.case_id}`)}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="outstanding">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base font-semibold text-slate-700">Outstanding Payments</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/admin/accounting/sales?status=pending')}>
                    View All <ChevronRight size={16} />
                  </Button>
                </CardHeader>
                <CardContent>
                  <DataTable 
                    columns={outstandingColumns} 
                    data={(outstanding?.data || []).slice(0, 5)}
                    emptyMessage="No outstanding payments"
                    onRowClick={(row) => row.case_id && navigate(`/admin/cases/${row.case_id}`)}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="expenses">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base font-semibold text-slate-700">Recent Expenses</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/admin/accounting/expenses')}>
                    View All <ChevronRight size={16} />
                  </Button>
                </CardHeader>
                <CardContent>
                  <DataTable 
                    columns={recentExpensesColumns} 
                    data={recentExpenses.slice(0, 5)}
                    emptyMessage="No expenses recorded yet"
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="forecast">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base font-semibold text-slate-700">
                    Upcoming Fixed Expenses (Next 3 Months)
                  </CardTitle>
                  <div className="text-sm text-slate-500">
                    Monthly Avg: {formatCurrency(forecast?.average_monthly_fixed)}
                  </div>
                </CardHeader>
                <CardContent>
                  <DataTable
                    columns={forecastColumns}
                    data={forecast?.forecast || []}
                    emptyMessage="No forecast data available"
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Contractor Payables Tab ──────────────────────────────── */}
            <TabsContent value="contractor">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
                      <UserCheck size={16} className="text-amber-600" />
                      Contractor Payables
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-0.5">Per-case fees owed to freelance case officers</p>
                  </div>
                  <div className="flex gap-2">
                    {['all','pending','paid'].map(f => (
                      <button key={f} onClick={() => setContractorFilter(f)}
                        className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${contractorFilter === f ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const filtered = contractorPayments.filter(p => contractorFilter === 'all' || p.payment_status === contractorFilter);
                    if (filtered.length === 0) return (
                      <div className="text-center py-10 text-slate-400 text-sm">
                        <UserCheck size={32} className="mx-auto mb-2 opacity-30" />
                        No contractor payments {contractorFilter !== 'all' ? `with status "${contractorFilter}"` : ''}
                      </div>
                    );
                    return (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-100">
                              <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Officer</th>
                              <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Case / Client</th>
                              <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Visa</th>
                              <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Terms</th>
                              <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Amount</th>
                              <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Status</th>
                              <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {filtered.map(p => (
                              <tr key={p.id} className="hover:bg-slate-50 transition-all">
                                <td className="py-3 px-3">
                                  <p className="font-medium text-slate-800">{p.staff_name}</p>
                                </td>
                                <td className="py-3 px-3">
                                  <p className="font-medium text-slate-700">{p.case_ucin || p.case_id?.slice(0,8)}</p>
                                  <p className="text-xs text-slate-400">{p.client_name}</p>
                                </td>
                                <td className="py-3 px-3 text-slate-600">{p.visa_type} · {p.country}</td>
                                <td className="py-3 px-3">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${p.payment_terms === 'prepayment' ? 'bg-blue-50 text-blue-700' : p.payment_terms === 'postpayment' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                    {p.payment_terms === 'prepayment' ? 'Prepayment' : p.payment_terms === 'postpayment' ? 'Postpayment' : 'Per-case'}
                                  </span>
                                </td>
                                <td className="py-3 px-3 text-right font-semibold text-slate-800">
                                  ₹{Number(p.amount).toLocaleString('en-IN')}
                                </td>
                                <td className="py-3 px-3">
                                  {p.payment_status === 'paid' ? (
                                    <span className="flex items-center gap-1 text-green-600 text-xs font-medium"><CheckCircle size={12} />Paid {p.paid_date ? `· ${p.paid_date}` : ''}</span>
                                  ) : p.payment_status === 'waived' ? (
                                    <span className="text-xs text-slate-400">Waived</span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-amber-600 text-xs font-medium"><Clock size={12} />Pending</span>
                                  )}
                                </td>
                                <td className="py-3 px-3">
                                  <div className="flex gap-1">
                                    {p.payment_status === 'pending' && (
                                      <button
                                        onClick={() => { setPayModal({ payment: p }); setPayForm({ method: 'Cash', reference: '' }); }}
                                        disabled={payingId === p.id}
                                        className="px-2 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                                      >
                                        {payingId === p.id ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle size={10} />}
                                        Mark Paid
                                      </button>
                                    )}
                                    {p.payment_status === 'paid' && (
                                      <button
                                        onClick={() => handleMarkUnpaid(p.id)}
                                        disabled={payingId === p.id}
                                        className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs font-medium hover:bg-slate-200 disabled:opacity-50"
                                      >
                                        Revert
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Pay Modal */}
          {payModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
                <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-bold text-slate-900">Mark Payment Paid</h3>
                  <button onClick={() => setPayModal(null)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4">
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-slate-500">Officer</span><span className="font-medium">{payModal.payment.staff_name}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-bold">₹{Number(payModal.payment.amount).toLocaleString('en-IN')}</span></div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Payment Method</label>
                    <select value={payForm.method} onChange={e => setPayForm(f => ({ ...f, method: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-slate-900/20 outline-none">
                      {['Cash','Bank Transfer','UPI','Cheque','Other'].map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Reference / UTR (optional)</label>
                    <input type="text" value={payForm.reference} onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))} placeholder="e.g. UTR12345" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-900/20 outline-none" />
                  </div>
                </div>
                <div className="px-5 py-4 border-t border-slate-200 flex gap-3">
                  <button onClick={() => setPayModal(null)} className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm hover:bg-slate-50">Cancel</button>
                  <button onClick={handleMarkPaid} disabled={!!payingId} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {payingId ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    Confirm Payment
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
